import { isNotNil } from "gather-common/dist/src/public/fpHelpers";
import { camelCase, cloneDeep, entries } from "lodash";
import { add, always, append, applySpec, chain, complement, concat, defaultTo, forEachObjIndexed, fromPairs, groupBy, has, is, isNil, map, mapObjIndexed, max, mean, mergeDeepRight, mergeWith, omit, pick, pipe, pluck, project, prop, reduce, reject, sum, toPairs, values, when, xprod, zipWith, } from "ramda";
import { calculateBitrate } from "../utils";
export const isRTCIceCandidateStats = (stats) => !isNil(stats) &&
    typeof stats === "object" &&
    has("type", stats) &&
    (stats.type === "local-candidate" || stats.type === "remote-candidate");
export const isRTCIceCandidatePairStats = (stats) => !isNil(stats) &&
    typeof stats === "object" &&
    has("type", stats) &&
    stats.type === "candidate-pair";
export const isFirefoxRTCIceCandidatePairStats = (stats) => isRTCIceCandidatePairStats(stats) && has("selected", stats);
export const isTransportStats = (stats) => !isNil(stats) && typeof stats === "object" && has("type", stats) && stats.type === "transport";
export const isRTCInboundRtpStreamStats = (stats) => !isNil(stats) && typeof stats === "object" && has("type", stats) && stats.type === "inbound-rtp";
export const isRTCRemoteOutboundRtpStreamStats = (stats) => !isNil(stats) &&
    typeof stats === "object" &&
    has("type", stats) &&
    stats.type === "remote-outbound-rtp";
export const isRTCOutboundRtpStreamStats = (stats) => !isNil(stats) && typeof stats === "object" && has("type", stats) && stats.type === "outbound-rtp";
export const isRTCRemoteInboundRtpStreamStats = (stats) => !isNil(stats) &&
    typeof stats === "object" &&
    has("type", stats) &&
    stats.type === "remote-inbound-rtp";
export const isRTCMediaSourceStats = (stats) => !isNil(stats) && typeof stats === "object" && has("type", stats) && stats.type === "media-source";
export const isRTCAudioSourceStats = (stats) => !isNil(stats) && typeof stats === "object" && has("kind", stats) && stats.kind === "audio";
const enrichWithCodecInfo = (statsMap, codecMimeTypes) => {
    Object.entries(statsMap).forEach(([key, stats]) => {
        const codecId = stats?.codecId;
        const mimeType = codecId ? codecMimeTypes[codecId] : undefined;
        if (mimeType) {
            statsMap[key] = {
                ...stats,
                codec: {
                    mimeType,
                },
            };
        }
    });
};
export function parseRTCStats(rtcStats) {
    const outputStats = {};
    const candidates = {};
    const candidatePairs = {};
    let selectedCandidatePairId;
    const inboundRtp = {};
    const remoteOutboundRtp = {};
    const outboundRtp = {};
    const remoteInboundRtp = {};
    const mediaSource = {};
    const codecs = {};
    Object.values(rtcStats).forEach((value) => {
        switch (value.type) {
            case "local-candidate":
            case "remote-candidate":
                if (isRTCIceCandidateStats(value)) {
                    candidates[value.id] = value;
                }
                break;
            case "candidate-pair":
                if (isRTCIceCandidatePairStats(value)) {
                    candidatePairs[value.id] = value;
                }
                if (isFirefoxRTCIceCandidatePairStats(value) && value.selected) {
                    selectedCandidatePairId = value.id;
                }
                break;
            case "transport":
                if (isTransportStats(value)) {
                    selectedCandidatePairId = value.selectedCandidatePairId;
                }
                break;
            case "inbound-rtp":
                if (isRTCInboundRtpStreamStats(value)) {
                    inboundRtp[value.id] = value;
                }
                break;
            case "remote-outbound-rtp":
                if (isRTCRemoteOutboundRtpStreamStats(value)) {
                    const localId = value.localId;
                    if (localId) {
                        remoteOutboundRtp[localId] = value;
                    }
                }
                break;
            case "outbound-rtp":
                if (isRTCOutboundRtpStreamStats(value)) {
                    outboundRtp[value.id] = value;
                }
                break;
            case "remote-inbound-rtp":
                if (isRTCRemoteInboundRtpStreamStats(value)) {
                    const localId = value.localId;
                    if (localId) {
                        remoteInboundRtp[localId] = value;
                    }
                }
                break;
            case "media-source":
                if (isRTCMediaSourceStats(value)) {
                    mediaSource[value.kind] = value;
                }
                break;
            case "codec":
                codecs[value.id] = value.mimeType;
                break;
            case "certificate":
            case "data-channel":
            case "peer-connection":
            case "track":
                break;
        }
    });
    enrichWithCodecInfo(inboundRtp, codecs);
    enrichWithCodecInfo(outboundRtp, codecs);
    if (Object.keys(codecs).length > 0) {
        outputStats["codecs"] = codecs;
    }
    if (Object.keys(inboundRtp).length > 0) {
        outputStats["inbound-rtp"] = inboundRtp;
    }
    if (Object.keys(remoteOutboundRtp).length > 0) {
        outputStats["remote-outbound-rtp"] = remoteOutboundRtp;
    }
    if (Object.keys(outboundRtp).length > 0) {
        outputStats["outbound-rtp"] = outboundRtp;
    }
    if (Object.keys(remoteInboundRtp).length > 0) {
        outputStats["remote-inbound-rtp"] = remoteInboundRtp;
    }
    if (Object.keys(mediaSource).length > 0) {
        outputStats["media-source"] = mediaSource;
    }
    const selectedPair = candidatePairs[selectedCandidatePairId || ""];
    if (selectedPair) {
        outputStats["candidate-pair"] = selectedPair;
    }
    const localCandidate = candidates[selectedPair?.localCandidateId || ""];
    if (localCandidate) {
        outputStats["local-candidate"] = localCandidate;
    }
    const remoteCandidate = candidates[selectedPair?.remoteCandidateId || ""];
    if (remoteCandidate) {
        outputStats["remote-candidate"] = remoteCandidate;
    }
    outputStats["candidates"] = candidates;
    return outputStats;
}
const fieldsToInclude = [
    "packetsLost",
    "packetsReceived",
    "packetsSent",
    "nackCount",
    "jitter",
    "pliCount",
    "firCount",
    "framesPerSecond",
    "framesEncoded",
    "keyFramesEncoded",
    "framesSent",
    "totalEncodeTime",
    "totalPacketSendDelay",
    "framesReceived",
    "framesDecoded",
    "keyFramesDecoded",
    "totalDecodeTime",
    "pauseCount",
    "totalPausesDuration",
    "freezeCount",
    "totalFreezesDuration",
    "timestamp",
    "timeDelta",
    "bitrateIn",
    "bitrateOut",
    "bytesSent",
    "bytesReceived",
    "totalBytesSent",
    "totalBytesReceived",
    "concealedSamples",
    "concealedSamplesDuration",
    "totalSamplesReceived",
    "totalSamplesDuration",
    "fractionConcealedSamples",
];
const fieldsToSubtract = [
    "packetsLost",
    "packetsReceived",
    "packetsSent",
    "nackCount",
    "pliCount",
    "firCount",
    "framesEncoded",
    "keyFramesEncoded",
    "framesSent",
    "totalEncodeTime",
    "totalPacketSendDelay",
    "framesReceived",
    "framesDecoded",
    "keyFramesDecoded",
    "totalDecodeTime",
    "pauseCount",
    "totalPausesDuration",
    "freezeCount",
    "totalFreezesDuration",
    "timeDelta",
    "bytesSent",
    "bytesReceived",
    "concealedSamples",
    "totalSamplesReceived",
    "totalSamplesDuration",
];
export function collectCodecsFromRtpData(data) {
    const mimeTypes = new Set();
    Object.values(data).forEach((rtpData) => {
        if (rtpData.codec?.mimeType) {
            mimeTypes.add(rtpData.codec.mimeType);
        }
    });
    return Array.from(mimeTypes).sort();
}
export function collectMetrics(prevRTCStats, currentStats) {
    const nextPrevStats = { send: {}, recv: {} };
    const perServerStats = {
        send: {},
        recv: {},
    };
    const candidateStats = {
        send: {},
        recv: {},
    };
    const candidateStatsArray = {
        send: {},
        recv: {},
    };
    const totalStats = {
        send: {},
        recv: {},
    };
    const totalStatsPerKind = {
        send: {
            screen: {},
            video: {},
            audio: {},
        },
        recv: {
            screen: {},
            video: {},
            audio: {},
        },
    };
    ["send", "recv"].forEach((direction) => {
        for (const serverURL in currentStats) {
            perServerStats[direction][serverURL] = {
                audio: {},
                video: {},
                screen: {},
            };
            const rtcStats = currentStats[serverURL]?.[direction];
            if (!rtcStats) {
                continue;
            }
            const parsedStats = parseRTCStats(rtcStats);
            const candidatePair = parsedStats["candidate-pair"];
            const inboundRtp = parsedStats["inbound-rtp"];
            const outboundRtp = parsedStats["outbound-rtp"];
            if (!candidatePair) {
                continue;
            }
            extractStatAsArray(candidatePair, candidateStatsArray, direction, direction === "recv" ? "availableIncomingBitrate" : "availableOutgoingBitrate");
            extractStatAsArray(candidatePair, candidateStatsArray, direction, "currentRoundTripTime");
            const rtpStat = direction === "send" ? outboundRtp : inboundRtp;
            if (!rtpStat) {
                continue;
            }
            const rtpStats = values(rtpStat).filter((i) => i.ssrc !== 1234);
            const groupByKind = groupBy((item) => {
                if (item.kind === "audio")
                    return "audio";
                if (item.kind === "video" && "contentType" in item && item.contentType === "screenshare")
                    return "screen";
                return "video";
            }, rtpStats);
            const computedFields = {
                timeDelta: prop("timestamp"),
                totalBytesReceived: prop("bytesReceived"),
                totalBytesSent: prop("bytesSent"),
            };
            const projectAndCompute = applySpec({
                ...fromPairs(map((field) => [field, prop(field)], ["id", ...fieldsToInclude])),
                ...computedFields,
            });
            entries(groupByKind).forEach(([kind, statsForKind]) => {
                const computedStats = statsForKind.map(projectAndCompute);
                computedStats.forEach((stats) => {
                    if (!stats.id)
                        return;
                    Object.assign(rtcStats[stats.id] ?? {}, stats);
                });
                const selectedBandwidthMetrics = map((stats) => {
                    const previousValues = stats.id
                        ? prevRTCStats[direction][serverURL]?.[stats.id]
                        : undefined;
                    return previousValues
                        ? subtractPreviousValuesFrom(fieldsToSubtract, previousValues, stats)
                        : stats;
                }, computedStats);
                const metrics = cleanse(mergeAndCollectProperties(initFields(fieldsToInclude, []), project(fieldsToInclude, selectedBandwidthMetrics)));
                const perServerStat = perServerStats[direction][serverURL];
                if (perServerStat) {
                    perServerStat[kind] = mergeWith(add, perServerStat[kind] ?? {}, metrics);
                }
            });
            nextPrevStats[direction][serverURL] = cloneDeep(rtcStats);
        }
        const collectAllStats = mergeAndCollectProperties({}, chain(values, values(perServerStats[direction])));
        const collectStatsPerScreen = collectStatsByKind(perServerStats[direction], "screen");
        const collectStatsPerVideo = collectStatsByKind(perServerStats[direction], "video");
        const collectStatsPerAudio = collectStatsByKind(perServerStats[direction], "audio");
        if (direction === "send") {
            totalStatsPerKind[direction]["screen"] = flattenMetrics(toPairs(createReport(direction, "screen")(collectStatsPerScreen)));
        }
        totalStatsPerKind[direction]["video"] = flattenMetrics(toPairs(createReport(direction, "video")(collectStatsPerVideo)));
        totalStatsPerKind[direction]["audio"] = flattenMetrics(toPairs(createReport(direction, "audio")(collectStatsPerAudio)));
        totalStats[direction] = mergeWith(addStats, totalStats[direction], flattenMetrics(toPairs(createReport(direction)(collectAllStats))));
        forEachObjIndexed((value, key) => {
            if (value) {
                candidateStats[direction][key] = mean(value.filter(isNotNil));
            }
        }, candidateStatsArray[direction]);
    });
    return {
        totalStats: mergeDeepRight(totalStats, candidateStats),
        totalStatsPerKind,
        nextPrevStats,
    };
}
const asArray = (arr) => (Array.isArray(arr) ? arr : [arr]);
const mergeAndCollectProperties = pipe(reduce(mergeWith((a, b) => concat(asArray(a), asArray(b)))), mapObjIndexed(asArray));
const cleanse = mapObjIndexed(pipe(defaultTo([]), reject(isNil)));
const flattenMetrics = reduce((acc, [key, value]) => ({
    ...acc,
    ...(is(Object)(value)
        ? fromPairs(toPairs(value).map(([k, v]) => [camelCase(`${key} ${k}`), v]))
        : { [key]: value }),
}), {});
const makeFinite = when(complement(isFinite), always(0));
const calcMeanMax = (value = []) => ({
    mean: defaultTo(0, mean(value)),
    max: defaultTo(0, makeFinite(Math.max(...value))),
});
const calcMean = (value = []) => ({
    mean: defaultTo(0, mean(value)),
});
const calcSum = (value = []) => ({
    sum: defaultTo(0, sum(value)),
});
const subtractPreviousValuesFrom = (fieldsToSubtract, previousValues, stat) => mergeWith((a, b) => max(0, a - b), omit(["id"], stat), pick(fieldsToSubtract, previousValues));
const initFields = (fields, initValue) => fromPairs(xprod(fields, [initValue]));
const transformProp = (propName, fn) => pipe(prop(propName), fn);
const sumWithDefault = pipe(defaultTo([]), sum);
const senderReportFields = (kind) => ({
    bytesSent: transformProp("bytesSent", sumWithDefault),
    bitrateOut: (value) => calculateBitrate(sumWithDefault(value["bytesSent"]), 0, makeFinite(Math.max(...(value["timeDelta"] ?? []))) / 1000),
    packetsSent: transformProp("packetsSent", sumWithDefault),
    totalBytesSent: transformProp("totalBytesSent", sumWithDefault),
    totalPacketSendDelay: transformProp("totalPacketSendDelay", calcSum),
    packetsLostPercentage: (value) => (sum(value["packetsLost"] ?? []) / sum(value["packetsSent"] ?? []) ?? 0) || 0,
    ...(!kind || ["video", "screen"].includes(kind)
        ? {
            framesEncoded: transformProp("framesEncoded", calcSum),
            keyFramesEncoded: transformProp("keyFramesEncoded", calcSum),
            framesSent: transformProp("framesSent", calcSum),
            totalEncodeTime: transformProp("totalEncodeTime", calcSum),
            deltaFramesEncodedSum: (value) => {
                const sumFramesEncoded = sumWithDefault(value["framesEncoded"]);
                const sumKeyFramesEncoded = sumWithDefault(value["keyFramesEncoded"]);
                return Math.max(sumFramesEncoded - sumKeyFramesEncoded, 0);
            },
        }
        : {}),
});
const receiverReportFields = (kind) => ({
    jitter: transformProp("jitter", calcMeanMax),
    bytesReceived: transformProp("bytesReceived", sumWithDefault),
    bitrateIn: (value) => calculateBitrate(sumWithDefault(value["bytesReceived"]), 0, makeFinite(Math.max(...(value["timeDelta"] ?? []))) / 1000),
    packetsReceived: transformProp("packetsReceived", sumWithDefault),
    totalBytesReceived: transformProp("totalBytesReceived", sumWithDefault),
    packetsLostPercentage: (value) => (sum(value["packetsLost"] ?? []) /
        (sum(value["packetsLost"] ?? []) + sum(value["packetsReceived"] ?? [])) ??
        0) ||
        0,
    ...(!kind || ["video", "screen"].includes(kind)
        ? {
            freezeCount: transformProp("freezeCount", calcSum),
            totalFreezesDuration: transformProp("totalFreezesDuration", calcSum),
            pauseCount: transformProp("pauseCount", calcSum),
            totalPausesDuration: transformProp("totalPausesDuration", calcSum),
            framesDecoded: transformProp("framesDecoded", calcSum),
            keyFramesDecoded: transformProp("keyFramesDecoded", calcSum),
            framesReceived: transformProp("framesReceived", calcSum),
            totalDecodeTime: transformProp("totalDecodeTime", calcSum),
            framesUndecoded: (value) => {
                const sumFramesDecoded = sumWithDefault(value["framesDecoded"]);
                const sumFramesReceived = sumWithDefault(value["framesReceived"]);
                if (sumFramesReceived === 0)
                    return 0;
                return 1 - sumFramesDecoded / sumFramesReceived;
            },
            deltaFramesDecodedSum: (value) => {
                const sumFramesDecoded = sumWithDefault(value["framesDecoded"]);
                const sumKeyFramesDecoded = sumWithDefault(value["keyFramesDecoded"]);
                return Math.max(sumFramesDecoded - sumKeyFramesDecoded, 0);
            },
        }
        : {}),
    ...(!kind || ["audio"].includes(kind)
        ? {
            concealedSamples: transformProp("concealedSamples", calcSum),
            concealedSamplesDuration: (value) => calcConcealedSamplesDuration(value),
            totalSamplesDuration: transformProp("totalSamplesDuration", calcSum),
            concealedSamplesFraction: (value) => {
                const concealedSamplesDuration = calcConcealedSamplesDuration(value);
                const totalSamplesDuration = sumWithDefault(value["totalSamplesDuration"]);
                return totalSamplesDuration !== 0 ? concealedSamplesDuration / totalSamplesDuration : 0;
            },
        }
        : {}),
});
const calcConcealedSamplesDuration = (value) => {
    const concealedSamplesArray = value["concealedSamples"] || [];
    const totalSamplesArray = value["totalSamplesReceived"] || [];
    const fractionConcealed = zipWith((concealedSamples, totalSamples) => concealedSamples / totalSamples, concealedSamplesArray, totalSamplesArray);
    const totalSampleDurations = value["totalSamplesDuration"] || [];
    const concealedSamplesDuration = zipWith((concealedFraction, duration) => concealedFraction * duration, fractionConcealed, totalSampleDurations);
    return sumWithDefault(concealedSamplesDuration);
};
const createReport = (direction, kind) => applySpec({
    timestamp: latestTimestamp,
    packetsLost: transformProp("packetsLost", sumWithDefault),
    nackCount: transformProp("nackCount", calcSum),
    ...(direction === "send" ? senderReportFields(kind) : receiverReportFields(kind)),
    ...(!kind || ["video", "screen"].includes(kind)
        ? {
            framesPerSecond: transformProp("framesPerSecond", calcMean),
            firCount: transformProp("firCount", calcSum),
            pliCount: transformProp("pliCount", calcSum),
        }
        : {}),
});
export const calculateFramesUndecoded = (previousStats = {}, nextStats) => {
    let framesUndecodedPercentage = 0;
    let { framesDecoded = 0, framesReceived = 0 } = nextStats;
    if (framesDecoded && previousStats.framesDecoded) {
        framesDecoded -= previousStats.framesDecoded;
    }
    if (framesReceived && previousStats?.framesReceived) {
        framesReceived -= previousStats.framesReceived;
    }
    if (framesReceived > 0) {
        framesUndecodedPercentage = 1 - framesDecoded / framesReceived;
    }
    return framesUndecodedPercentage;
};
export const calculateConcealedSamplesDuration = (previousStats = {}, nextStats) => {
    const concealedSamples = (nextStats.concealedSamples || 0) - (previousStats.concealedSamples || 0);
    const totalSamplesReceived = (nextStats.totalSamplesReceived || 0) - (previousStats.totalSamplesReceived || 0);
    const concealedSamplesFraction = totalSamplesReceived !== 0 ? concealedSamples / totalSamplesReceived : 0;
    const totalSamplesDurationS = (nextStats.totalSamplesDuration || 0) - (previousStats.totalSamplesDuration || 0);
    const concealedSamplesDurationS = concealedSamplesFraction * totalSamplesDurationS;
    return {
        concealedSamplesDurationS: concealedSamplesDurationS,
        totalSamplesDurationS: totalSamplesDurationS,
    };
};
const addStats = (a, b) => add(defaultTo(0, a), defaultTo(0, b));
function latestTimestamp(value) {
    return makeFinite(Math.max(...(value?.timestamp ?? [])));
}
function extractStatAsArray(candidatePair, candidateStatsArray, direction, candidateProperty) {
    const candidatePairValue = candidatePair[candidateProperty];
    if (candidatePairValue) {
        const hasProperty = candidateStatsArray[direction][candidateProperty];
        candidateStatsArray[direction][candidateProperty] = hasProperty
            ? append(candidatePairValue, asArray(candidateStatsArray[direction][candidateProperty]))
            : [candidatePairValue];
    }
}
function collectStatsByKind(perServerStat, kind) {
    return mergeAndCollectProperties({}, pluck(kind, values(perServerStat)).filter(isNotNil));
}
//# sourceMappingURL=rtcStats.js.map